/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { messages } from './messages';

/**
 * Which install-instructions variant the section is currently showing.
 *
 * - `cli`: the OS-aware terminal mockup with shell install commands.
 * - `mcp`: the MCP server URL with the MCP tool catalogue.
 */
export type Tab = 'cli' | 'mcp';

export type TabToggleProps = {
	selectedTab: Tab;
	onSelectTab: (tab: Tab) => void;
};

const styles = cssMap({
	tabToggle: {
		display: 'inline-flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		gap: token('space.050'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '10px',
	},
	tabButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		width: '72px',
		height: '36px',
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.full'),
		borderWidth: '1.5px',
		borderStyle: 'solid',
		backgroundColor: 'transparent',
		font: token('font.body'),
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	tabButtonActive: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#B7B9BE',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#B7B9BE',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#101214',
	},
	tabButtonInactive: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
	},
});

export const TabToggle = ({ selectedTab, onSelectTab }: TabToggleProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const isCliActive = selectedTab === 'cli';

	return (
		<div css={styles.tabToggle} role="group" aria-label={formatMessage(messages.heading)}>
			<button
				type="button"
				aria-label={formatMessage(messages.tabCliAriaLabel)}
				aria-pressed={isCliActive}
				css={[styles.tabButton, isCliActive ? styles.tabButtonActive : styles.tabButtonInactive]}
				onClick={() => onSelectTab('cli')}
			>
				<FormattedMessage {...messages.tabCli} />
			</button>
			<button
				type="button"
				aria-label={formatMessage(messages.tabMcpAriaLabel)}
				aria-pressed={!isCliActive}
				css={[styles.tabButton, !isCliActive ? styles.tabButtonActive : styles.tabButtonInactive]}
				onClick={() => onSelectTab('mcp')}
			>
				<FormattedMessage {...messages.tabMcp} />
			</button>
		</div>
	);
};
